/**
 * This function renders an image input
 * @param {image}    image     It is the value of the current image
 * @param {setImage} setImage   It is used to  set the image value
 * @param {currImage} currImage If you want to show a default image
 * @param {name} name give a name to access the input
 * @param {validHeight} validHeight checks the height of the image should be equal to this
 * @param {validWidth} validWidth checks the width of the image should be equal to this
 * @param {maximumWidth} maximumWidth checks the width of the image should be less than this
 * @param {maximumHeight} maximumHeight  checks the height of the image should be less than this
 *
 */

import { Typography, styled } from "@mui/material";
import { CameraImagePropTypes } from "../../../../../types";
import { useEffect, useRef, useState } from "react";
import { openPopup } from "../../../../../stateManagement/actions/popupActions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { faCamera } from "@fortawesome/free-solid-svg-icons";
import defaultLogo from "../../../../../assets/image/png/salescode-logo.png";


export const CameraImageUploader = (props: CameraImagePropTypes) => {
  // 
  const Wrapper = styled("span")(`
  border: grey dashed 1px;
  border-radius:4%;
  background-color:#D6DCDD;
  display:flex;
  justify-content:center;
  flex-direction:column;
  align-items:center;
  position: relative; /* Make Wrapper relative for positioning the remove button */
  width:${props.width || "12rem"};
  height: ${props.height || "7.5rem"};
  color:#414646;
  gap:0.3rem;
  cursor:pointer;
`);

const SelectedImage = styled("img")(`
  height:2.8rem;
`);

const RemoveButton = styled("span")(`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background-color: #227C9D;
  color: white;
  border-radius: 50%;
  width: 1rem;
  height: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 10; /* Ensure the button appears above the image */
`);

const {
  image,
  setImage,
  currImage,
  name,
  uploadText,
  validWidth,
  validHeight,
  maximumHeight,
  maximumWidth,
  disable,
  removeButton,
  removeFunction,
  maxSize,
  nameValidation,
} = props;

const [showImage, setShowImage] = useState<string>();
const imageRef = useRef<HTMLInputElement>(null);
const showImageRef = useRef<HTMLImageElement>(null);

const handleChange = () => {
  if (disable) {
    return;
  }
  if (imageRef?.current) imageRef.current.click();
};

useEffect(()=>{
  setImage(currImage)
  setShowImage(currImage)
},[currImage])
const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  if (disable) {
    return;
  }
  e.preventDefault();
  if (e.target.files && e.target.files[0]) {
    let reader = new FileReader();
    reader.readAsDataURL(e.target.files[0]);
    let img = e.target.files[0];
    reader.onload = function (e) {
      let image: HTMLImageElement = new Image();
      image.src = e?.target?.result as string;
      image.onload = function () {
        let height = image.height;
        let width = image.width;

        if (maxSize && img.size > maxSize * 1024) {
          setImage("");
          setShowImage("");
          openPopup(
            "Error",
            `The image size should be less than ${maxSize} KB`
          );
          return;
        }

        const nameValidationResponse = nameValidation
          ? nameValidation(img.name)
          : undefined;
        if (
          nameValidationResponse &&
          nameValidationResponse.status === "failed"
        ) {
          setImage("");
          setShowImage("");
          openPopup(
            "Error",
            nameValidationResponse.message ?? "Name Validation Failure"
          );
          return;
        }

        if (validHeight || validWidth || maximumHeight || maximumWidth) {
          if (validHeight && validHeight !== height) {
            setImage("");
            setShowImage("");
            openPopup("Error", `The image size should have ${validHeight} height`);
          } else if (validWidth && validWidth !== width) {
            setImage("");
            setShowImage("");
            openPopup("Error", `The image size should have ${validWidth} width`);
          } else if (maximumHeight && height && height > maximumHeight) {
            setImage("");
            setShowImage("");
            openPopup("Error", `The image size should have ${maximumHeight} maximum height`);
          } else if (maximumWidth && width && width > maximumWidth) {
            setImage("");
            setShowImage("");
            openPopup("Error", `The image size should have ${maximumWidth} maximum width`);
          } else {
            setImage(img);
            setShowImage(URL.createObjectURL(img));
            return true;
          }
        } else {
          setImage(img);
          setShowImage(URL.createObjectURL(img));
          return true;
        }
      };
    };
    reader.onerror = function () {
      setImage("");
      setShowImage("");
      openPopup("Error", `Error occurred`);
    };
  }
};

return (
  <Wrapper onClick={handleChange}>
    {showImage && image ? (
      <>
        <SelectedImage
          ref={showImageRef}
          src={showImage}
          className="selectedImage"
        />
        {removeButton && (
          <RemoveButton onClick={removeFunction}>×</RemoveButton>
        )}
      </>
    ) : currImage ? (
      <>
        <SelectedImage src={currImage} className="selectedImage" />
        {removeButton && (
          <RemoveButton onClick={removeFunction}>×</RemoveButton>
        )}
      </>
    ) : (
      // <FontAwesomeIcon
      //   icon={faCamera}
      //   color="var(--dashboard-secondary-color)"
      //   style={{ width: "2.5rem", height: "2.8rem" }}
      // />
      <SelectedImage src={defaultLogo} className="selectedImage" />
    )}
    <input
      {...props}
      onChange={handleFileChange}
      type="file"
      name={name ? name : "file"}
      ref={imageRef}
      style={{ display: "none" }}
      accept="image/*"
    />
    {(!props.currImage && !props.image) && (
        <Typography className="genericImageUploadMessage" variant="subtitle2" color="#414646">
          {props.uploadText ?? "Please Upload An Image"}
        </Typography>)}
  </Wrapper>
);
};
